import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { fetchAllCustomers } from '../api';
import Layout from '../components/Layout';
import CustomerModal from './CustomerModal';
import PageHeader from './PageHeader';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Snackbar,
  Alert,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const CustomerPage = () => {
  const [loading, setLoading] = useState(true);
  const [customerModalVisibility, setCustomerModalVisibility] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [allCustomers, setAllCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const CUSTOMERS_PER_PAGE = 5;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const all = await fetchAllCustomers();
      setAllCustomers(all);
    } catch (error) {
      setMessage('Error fetching customers.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setLoading(false);
  };

  // Filter customers based on search term
  const filteredCustomers = allCustomers.filter((customer) => {
    return (
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Pagination logic (frontend)
  const totalPages = Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * CUSTOMERS_PER_PAGE,
    currentPage * CUSTOMERS_PER_PAGE
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const toggleCustomerModalVisibility = () => {
    setCustomerModalVisibility(!customerModalVisibility);
  };

  if (loading) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <PageHeader title="Customers" subtitle="Manage your customers." />
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Customer List</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={toggleCustomerModalVisibility}>
                Add new customer
              </Button>
            </Box>
            {/* Search Input - always visible, modal overlays on top */}
            <Box mb={3} display="flex" justifyContent="center">
              <TextField
                label={'Search customers by name, email, phone, or address...'}
                // label={customerModalVisibility ? '' : 'Search customers by name, email, phone, or address...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                sx={{ width: '60%', maxWidth: 400 }}
                InputLabelProps={{ shrink: !!searchTerm || !customerModalVisibility }}
              />
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>{customer.address}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination Controls (frontend) */}
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </CardContent>
        </Card>
        <CustomerModal
          isOpen={customerModalVisibility}
          onClose={toggleCustomerModalVisibility}
        />
        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default CustomerPage;
